/**
 * @fileoverview Hooks for the system inspection-program + schedule-rule-catalog
 *   admin surface (issue #737 Phase 6, plan §11.5 step 59).
 * @module system-web/hooks/useSystemInspectionPrograms
 *
 * Responsibilities:
 * - Subscribe to the scheduling catalogs from Dexie via liveQuery.
 * - Expose mutations that route through system-api (which propagates to
 *   tenants) then put the response into Dexie for immediate feedback.
 *
 * Dependencies:
 * - dexie/liveQuery
 * - ../services/systemInspectionProgramService
 * - ../services/scheduleRuleCatalogService
 *
 * Architecture:
 * - liveQuery observed functions are pure Dexie reads; mutations are
 *   imperative and NEVER called inside liveQuery.
 * - Server-authoritative: no optimistic Dexie writes before the API responds.
 *
 * @localFirst
 * Source of Truth: hybrid — Dexie for reads, system-api for writes.
 * Write Flow: service -> apiClient -> Dexie put; liveQuery re-emits.
 * Sync Strategy: read-only pull.
 * Conflict Resolution: server-authoritative.
 */

import { useCallback, useEffect, useState } from 'react';
import { liveQuery } from 'dexie';
import {
  createSystemInspectionProgram,
  listSystemInspectionPrograms,
  propagateSchedulingCatalogs,
  setSystemInspectionProgramActive,
  updateSystemInspectionProgram,
  type SchedulingPropagationSummary,
  type SystemInspectionProgram,
  type SystemInspectionProgramPatch,
} from '../services/systemInspectionProgramService';
import {
  createScheduleRuleCatalogEntry,
  listScheduleRuleCatalog,
  updateScheduleRuleCatalogEntry,
  type ScheduleRuleCatalogEntry,
  type ScheduleRuleCatalogPatch,
} from '../services/scheduleRuleCatalogService';

/**
 * @process ResilientLiveQuerySubscription
 * Steps:           Subscribe to a liveQuery; on error, surface it and
 *                  resubscribe after a delay instead of leaving the
 *                  observable dead.
 * Inputs:          querier — pure Dexie read; onNext/onError callbacks; tag —
 *                  log prefix identifying the hook.
 * Outputs:         Cleanup function for useEffect.
 * Failure Handling:Dexie terminates a liveQuery observable after its first
 *                  error emit — without resubscription one transient failure
 *                  (DB mid-open, aborted transaction) silently freezes the
 *                  UI at the last emission until a manual refresh. A bounded
 *                  1s retry keeps live updates alive and still surfaces the
 *                  error to the page.
 * Idempotency:     REQUIRED — resubscription re-reads; no side effects.
 */
function subscribeResilient<T>(
  querier: () => Promise<T>,
  onNext: (value: T) => void,
  onError: (err: unknown) => void,
  tag: string,
): () => void {
  let subscription: { unsubscribe(): void } | undefined;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let disposed = false;
  const subscribe = () => {
    subscription = liveQuery(querier).subscribe({
      next: onNext,
      error: (err) => {
        console.error(`[${tag}] liveQuery error — resubscribing:`, err);
        onError(err);
        if (!disposed) retryTimer = setTimeout(subscribe, 1000);
      },
    });
  };
  subscribe();
  return () => {
    disposed = true;
    if (retryTimer) clearTimeout(retryTimer);
    subscription?.unsubscribe();
  };
}

/**
 * @hook useSystemInspectionPrograms
 * Purpose: Live predefined-program catalog + program mutations for the
 *          system admin surface.
 * Returns: programs, isLoading, isMutating, error, addProgram, updateProgram,
 *          setProgramActive, propagate, clearError.
 * Side Effects: liveQuery subscription on mount (auto-resubscribes on error);
 *               mutations call system-api.
 * Offline Behavior: Dexie list available offline; mutations require connectivity.
 */
export function useSystemInspectionPrograms() {
  const [programs, setPrograms] = useState<SystemInspectionProgram[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    return subscribeResilient(
      listSystemInspectionPrograms,
      (rows) => {
        setPrograms(rows);
        setIsLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err.message : 'Failed to load inspection programs');
        setIsLoading(false);
      },
      'useSystemInspectionPrograms',
    );
  }, []);

  const addProgram = useCallback(
    async (input: SystemInspectionProgramPatch & { programKey?: string }) => {
      setIsMutating(true);
      setError(null);
      try {
        return await createSystemInspectionProgram(input);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create program');
        return null;
      } finally {
        setIsMutating(false);
      }
    },
    [],
  );

  const updateProgram = useCallback(async (id: string, patch: SystemInspectionProgramPatch) => {
    setIsMutating(true);
    setError(null);
    try {
      return await updateSystemInspectionProgram(id, patch);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update program');
      return null;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const setProgramActive = useCallback(async (id: string, active: boolean) => {
    setIsMutating(true);
    setError(null);
    try {
      return await setSystemInspectionProgramActive(id, active);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle program');
      return null;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const propagate = useCallback(
    async (
      scope: 'inspectionPrograms' | 'scheduleRuleCatalog' | 'all' = 'all',
    ): Promise<SchedulingPropagationSummary | null> => {
      setIsMutating(true);
      setError(null);
      try {
        return await propagateSchedulingCatalogs(scope);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to propagate catalogs');
        return null;
      } finally {
        setIsMutating(false);
      }
    },
    [],
  );

  return {
    programs,
    isLoading,
    isMutating,
    error,
    addProgram,
    updateProgram,
    setProgramActive,
    propagate,
    clearError: () => setError(null),
  };
}

/**
 * @hook useScheduleRuleCatalog
 * Purpose: Live rule-type catalog + entry mutations for the system admin surface.
 * Returns: entries, isLoading, isMutating, error, addEntry, updateEntry, clearError.
 * Side Effects: liveQuery subscription on mount (auto-resubscribes on error);
 *               mutations call system-api.
 * Offline Behavior: Dexie list available offline; mutations require connectivity.
 */
export function useScheduleRuleCatalog() {
  const [entries, setEntries] = useState<ScheduleRuleCatalogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIsLoading(true);
    return subscribeResilient(
      listScheduleRuleCatalog,
      (rows) => {
        setEntries(rows);
        setIsLoading(false);
      },
      (err) => {
        setError(err instanceof Error ? err.message : 'Failed to load rule catalog');
        setIsLoading(false);
      },
      'useScheduleRuleCatalog',
    );
  }, []);

  const addEntry = useCallback(
    async (input: { ruleTypeCode: string; name: string } & ScheduleRuleCatalogPatch) => {
      setIsMutating(true);
      setError(null);
      try {
        return await createScheduleRuleCatalogEntry(input);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to create catalog entry');
        return null;
      } finally {
        setIsMutating(false);
      }
    },
    [],
  );

  const updateEntry = useCallback(async (id: string, patch: ScheduleRuleCatalogPatch) => {
    setIsMutating(true);
    setError(null);
    try {
      return await updateScheduleRuleCatalogEntry(id, patch);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update catalog entry');
      return null;
    } finally {
      setIsMutating(false);
    }
  }, []);

  return {
    entries,
    isLoading,
    isMutating,
    error,
    addEntry,
    updateEntry,
    clearError: () => setError(null),
  };
}
